/**
 * security-page.js — Sanity integration for the Security V4 page
 */

import { client, urlFor, getSanityEditLink, resolveInternalLink } from './sanity-client';

const QUERY = `*[_type == "securityPage" && slug.current == "security"][0]{
  _id, title, slug,
  showHero, showProductDev, showConsulting, showEvaluation, showCaseStudies, showCta,
  heroDescription,
  stackLayers[]{ _key, name, primitives, emphasized },
  pdHeading, pdLede,
  consultingHeading, consultingLede,
  consultingTiles[]{ _key, ix, heading, body },
  evalHeading, evalLede,
  evalRows[]{ _key, num, heading, body, vecLabel, vecMeta, vecWidth },
  "caseStudies": caseStudies[@->visibility != false]->{ _id, title, "slug": slug.current, image, description },
  ctaHeadline, ctaLede, ctaLocations
}`;

function parseAccent(text) {
  if (!text) return '';
  return text.replace(/\*([^*]+)\*/g, '<em style="font-style:normal;color:var(--se4-accent);font-weight:700">$1</em>');
}

function setField(attr, value, html = false) {
  const el = document.querySelector(`[data-se4="${attr}"]`);
  if (!el || !value) return;
  if (html) el.innerHTML = value;
  else el.textContent = value;
}

function setVisible(id, visible) {
  const el = document.getElementById(id);
  if (!el) return;
  el.style.display = visible === false ? 'none' : '';
}

function attachRevealObserver(els) {
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
  els.forEach(el => io.observe(el));
}


function renderStackLayers(layers) {
  const body = document.getElementById('se4-stack-layers');
  if (!body || !layers || !layers.length) return;
  const spineDot = body.querySelector('.se4-spine-dot');
  body.innerHTML = '';
  if (spineDot) body.appendChild(spineDot);
  layers.forEach(layer => {
    const div = document.createElement('div');
    div.className = 'se4-layer' + (layer.emphasized ? ' is-emph' : '');
    div.innerHTML = `<span class="name">${layer.name || ''}</span><span class="chip">${layer.primitives || ''}</span>`;
    body.appendChild(div);
  });
}

function renderHero(data, docId) {
  if (data.title) {
    const el = document.querySelector('[data-se4="title"]');
    if (el) el.innerHTML = `${data.title}<span class="dot">.</span>`;
  }
  setField('heroDescription', data.heroDescription);
  if (data.stackLayers && data.stackLayers.length) renderStackLayers(data.stackLayers);

  const section = document.getElementById('se4-hero');
  if (section && docId) {
    section.classList.add('position-relative');
    section.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'securityPage', 'heroDescription', 'Edit Hero'));
  }
}

function renderProductDev(data, docId) {
  if (data.pdHeading) {
    const el = document.querySelector('[data-se4="pdHeading"]');
    if (el) el.innerHTML = parseAccent(data.pdHeading);
  }
  setField('pdLede', data.pdLede);

  const section = document.getElementById('se4-product-dev');
  if (section && docId) {
    section.classList.add('position-relative');
    section.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'securityPage', 'pdHeading', 'Edit Product Dev'));
  }
}

function renderConsulting(data, docId) {
  if (data.consultingHeading) {
    const el = document.querySelector('[data-se4="consultingHeading"]');
    if (el) el.innerHTML = parseAccent(data.consultingHeading);
  }
  setField('consultingLede', data.consultingLede);

  if (data.consultingTiles && data.consultingTiles.length) {
    const tilesEl = document.getElementById('se4-consulting-tiles');
    if (tilesEl) {
      tilesEl.innerHTML = data.consultingTiles.map((tile, i) => {
        const ghostNum = String(i + 1).padStart(2, '0');
        return `
          <article class="se4-tile" data-reveal style="--rd:${i * 120}ms">
            <span class="ghost-num" aria-hidden="true">${ghostNum}</span>
            <span class="ix">${tile.ix || ''}</span>
            <h3>${tile.heading || ''}</h3>
            <p>${tile.body || ''}</p>
          </article>`;
      }).join('');
      attachRevealObserver(tilesEl.querySelectorAll('[data-reveal]'));
    }
  }

  const section = document.getElementById('se4-consulting');
  if (section && docId) {
    section.classList.add('position-relative');
    section.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'securityPage', 'consultingHeading', 'Edit Consulting'));
  }
}

function renderEvaluation(data, docId) {
  if (data.evalHeading) {
    const el = document.querySelector('[data-se4="evalHeading"]');
    if (el) el.innerHTML = parseAccent(data.evalHeading);
  }
  setField('evalLede', data.evalLede);

  if (data.evalRows && data.evalRows.length) {
    const listEl = document.getElementById('se4-eval-rows');
    if (listEl) {
      listEl.innerHTML = data.evalRows.map((row, i) => `
        <article class="se4-check-row" data-reveal data-vec>
          <span class="num">${row.num || String(i + 1).padStart(2, '0')}</span>
          <div>
            <h3>${row.heading || ''}</h3>
            <p>${row.body || ''}</p>
          </div>
          <div class="vec">
            <div class="se4-vec-bar"><span class="fill" style="--w:${row.vecWidth || 75}%"></span></div>
            <div class="se4-vec-meta"><span class="lbl">${row.vecLabel || ''}</span><span>${row.vecMeta || ''}</span></div>
          </div>
        </article>`).join('');
      attachRevealObserver(listEl.querySelectorAll('[data-reveal]'));
      initVecObserver();
    }
  }

  const section = document.getElementById('se4-evaluation');
  if (section && docId) {
    section.classList.add('position-relative');
    section.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'securityPage', 'evalHeading', 'Edit Evaluation'));
  }
}

function renderCaseStudies(caseStudies, docId) {
  const section = document.getElementById('se4-case-studies');
  if (section && docId) {
    section.classList.add('position-relative');
    section.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'securityPage', 'caseStudies', 'Edit Case Studies'));
  }

  const grid = document.getElementById('se4-cs-grid');
  if (!grid || !caseStudies || !caseStudies.length) return;

  const artBgs = [
    `radial-gradient(120% 80% at 90% 90%, rgba(229,50,45,.30), transparent 55%),
     radial-gradient(60% 60% at 10% 10%, rgba(64,180,255,.20), transparent 60%),
     linear-gradient(135deg, #0F1114 0%, #1B1F25 60%, #0A0B0D 100%)`,
    `radial-gradient(120% 80% at 10% 90%, rgba(31,184,166,.25), transparent 55%),
     radial-gradient(60% 60% at 90% 10%, rgba(120,180,255,.18), transparent 60%),
     linear-gradient(135deg, #0F1114 0%, #14181E 60%, #0A0B0D 100%)`,
    `radial-gradient(120% 80% at 50% 100%, rgba(229,50,45,.20), transparent 55%),
     radial-gradient(60% 60% at 50% 0%, rgba(31,184,166,.15), transparent 60%),
     linear-gradient(135deg, #0F1114 0%, #161A20 60%, #0A0B0D 100%)`,
  ];

  const glyphs = [
    `<svg viewBox="0 0 200 200" fill="none" stroke="currentColor" stroke-width="1.4">
       <path d="M100 14 L172 44 V104 C172 144 140 174 100 188 C60 174 28 144 28 104 V44 Z"/>
       <path d="M70 100 L92 122 L130 84"/>
     </svg>`,
    `<svg viewBox="0 0 200 200" fill="none" stroke="currentColor" stroke-width="1.4">
       <circle cx="64" cy="100" r="26"/>
       <path d="M86 100 H170"/>
       <path d="M150 100 V120"/><path d="M132 100 V116"/><path d="M170 100 V124"/>
       <circle cx="64" cy="100" r="6" fill="currentColor"/>
     </svg>`,
    `<svg viewBox="0 0 200 200" fill="none" stroke="currentColor" stroke-width="1.4">
       <rect x="36" y="60" width="48" height="40" rx="3"/>
       <rect x="116" y="60" width="48" height="40" rx="3"/>
       <rect x="36" y="120" width="48" height="40" rx="3"/>
       <rect x="116" y="120" width="48" height="40" rx="3"/>
       <path d="M84 80 H116 M84 140 H116 M60 100 V120 M140 100 V120" stroke-dasharray="3 4"/>
       <circle cx="100" cy="80" r="4" fill="currentColor"/>
       <circle cx="100" cy="140" r="4" fill="currentColor"/>
     </svg>`,
  ];

  grid.innerHTML = caseStudies.map((cs, i) => {
    const href = cs.title
      ? resolveInternalLink(`/case-studies/case-study.html?title=${encodeURIComponent(cs.title)}`)
      : '#';
    const hasImage = cs.image && cs.image.asset;
    const imgUrl = hasImage ? urlFor(cs.image).width(800).quality(80).url() : '';
    const artStyle = hasImage
      ? `style="background-image:linear-gradient(180deg, rgba(0,0,0,.15), rgba(0,0,0,.65)), url('${imgUrl}');background-size:cover;background-position:center"`
      : `style="background:${artBgs[i % artBgs.length]}"`;
    return `
      <a href="${href}" class="se4-cs-card" data-reveal style="--rd:${i * 120}ms">
        <div class="art" ${artStyle}>
          <div class="art-grid"></div>
          ${hasImage ? '' : `<div class="art-glyph">${glyphs[i % glyphs.length]}</div>`}
        </div>
        <div class="read" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
        </div>
        <span class="ribbon"></span>
        <div class="meta">
          <span class="cs-tag">Security</span>
          <h3>${cs.title || ''}</h3>
        </div>
      </a>`;
  }).join('');

  attachRevealObserver(grid.querySelectorAll('[data-reveal]'));
}

function renderCta(data, docId) {
  if (data.ctaHeadline) {
    const el = document.querySelector('[data-se4="ctaHeadline"]');
    if (el) el.innerHTML = parseAccent(data.ctaHeadline);
  }
  setField('ctaLede', data.ctaLede);
  setField('ctaLocations', data.ctaLocations);

  const section = document.getElementById('se4-cta');
  if (section && docId) {
    section.classList.add('position-relative');
    section.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'securityPage', 'ctaHeadline', 'Edit CTA'));
  }
}

function initNavbarScroll() {
  const navbar = document.querySelector('.navbar');
  if (!navbar) return;
  function onScroll() {
    if (window.scrollY > 50) {
      navbar.classList.add('navbar-v3-scrolled');
      navbar.classList.remove('navbar-v3-transparent');
    } else {
      navbar.classList.remove('navbar-v3-scrolled');
      navbar.classList.add('navbar-v3-transparent');
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

function initStaticReveal() {
  const wrap = document.getElementById('se4-root');
  if (wrap) wrap.classList.add('js-reveal-ready');
  const els = document.querySelectorAll('.se4-wrap [data-reveal]');
  attachRevealObserver(els);
  setTimeout(() => {
    document.querySelectorAll('.se4-wrap [data-reveal]:not(.in)').forEach(el => el.classList.add('in'));
  }, 1200);
}

function initCsRail() {
  const rail = document.getElementById('se4-cs-rail');
  const grid = document.getElementById('se4-cs-grid');
  if (!rail || !grid) return;
  const prev = rail.querySelector('.se4-cs-arrow.prev');
  const next = rail.querySelector('.se4-cs-arrow.next');

  function updateArrows() {
    if (prev) prev.disabled = grid.scrollLeft <= 0;
    if (next) next.disabled = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 1;
  }
  function checkOverflow() {
    rail.classList.toggle('has-overflow', grid.scrollWidth > grid.clientWidth);
    updateArrows();
  }
  if (prev) prev.addEventListener('click', () => {
    const card = grid.querySelector('.se4-cs-card');
    grid.scrollBy({ left: -(card ? card.offsetWidth + 20 : 300), behavior: 'smooth' });
  });
  if (next) next.addEventListener('click', () => {
    const card = grid.querySelector('.se4-cs-card');
    grid.scrollBy({ left: card ? card.offsetWidth + 20 : 300, behavior: 'smooth' });
  });
  grid.addEventListener('scroll', updateArrows, { passive: true });
  new ResizeObserver(checkOverflow).observe(grid);
  checkOverflow();
}

document.addEventListener('DOMContentLoaded', async () => {
  initNavbarScroll();
  initStaticReveal();
  initCsRail();

  let data;
  try {
    data = await client.fetch(QUERY);
  } catch (err) {
    console.warn('[security-page] Sanity fetch failed — using static HTML fallback.', err);
    return;
  }

  if (!data) {
    console.warn('[security-page] No Sanity document found for slug "security".');
    return;
  }

  const docId = data._id;

  setVisible('se4-hero',         data.showHero);
  setVisible('se4-product-dev',  data.showProductDev);
  setVisible('se4-consulting',   data.showConsulting);
  setVisible('se4-evaluation',   data.showEvaluation);
  setVisible('se4-case-studies', data.showCaseStudies);
  setVisible('se4-cta',          data.showCta);

  if (data.showHero !== false)         renderHero(data, docId);
  if (data.showProductDev !== false)   renderProductDev(data, docId);
  if (data.showConsulting !== false)   renderConsulting(data, docId);
  if (data.showEvaluation !== false)   renderEvaluation(data, docId);
  if (data.showCaseStudies !== false) {
    renderCaseStudies(data.caseStudies || [], docId);
    if (data.caseStudies && data.caseStudies.length) initCsRail();
  }
  if (data.showCta !== false)          renderCta(data, docId);
});
